/**
 * DashboardHeader Component
 *
 * Enhanced branded header for the Stock Sentry dashboard with:
 * - Stock Sentry logo and brand name
 * - StatCard components with semantic coloring and trends
 * - Hover effects and smooth transitions
 * - Responsive layout with proper wrapping
 */

import type { RefObject } from 'react';
import type { IconSource } from '@shopify/polaris';
import {
  PackageIcon,
  AlertTriangleIcon,
  AlertCircleIcon,
  CheckCircleIcon,
} from '@shopify/polaris-icons';
import { StockSentryLogo } from '../ui/StockSentryLogo';
import { RetailForgeLogo } from '../ui/RetailForgeLogo';
import { StatCard } from './StatCard';
import type { TrendData } from './TrendIndicator';
import type { StatCardType } from './StatCardActions';
import type { DashboardStatistics } from '../../types/dashboard';

/**
 * Stat configuration for cards
 */
interface StatConfig {
  id: StatCardType;
  label: string;
  icon: IconSource;
  getValue: (stats: DashboardStatistics) => number;
  tabIndex: number;
}

const STAT_CONFIGS: StatConfig[] = [
  {
    id: 'monitored',
    label: 'Monitored',
    icon: PackageIcon,
    getValue: (stats) => stats.productsMonitored,
    tabIndex: 1,
  },
  {
    id: 'critical',
    label: 'Critical',
    icon: AlertTriangleIcon,
    getValue: (stats) => stats.criticalAlerts,
    tabIndex: 0,
  },
  {
    id: 'warning',
    label: 'Warning',
    icon: AlertCircleIcon,
    getValue: (stats) => stats.warningAlerts,
    tabIndex: 0,
  },
  {
    id: 'healthy',
    label: 'Healthy',
    icon: CheckCircleIcon,
    getValue: (stats) => stats.healthyStock,
    tabIndex: 1,
  },
];

/**
 * Trend data for all stat types
 */
export interface StatTrends {
  monitored?: TrendData;
  critical?: TrendData;
  warning?: TrendData;
  healthy?: TrendData;
}

export interface DashboardHeaderProps {
  /** Statistics to display in cards */
  statistics: DashboardStatistics;
  /** Optional trend data for period comparisons */
  trends?: StatTrends;
  /** Callback when a stat card is clicked */
  onStatClick?: (tabIndex: number, ref?: RefObject<HTMLDivElement>) => void;
  /** Optional ref to scroll to when clicking stats */
  scrollRef?: RefObject<HTMLDivElement>;
}

/**
 * DashboardHeader - Enhanced branded header with logo and stat cards
 */
export function DashboardHeader({
  statistics,
  trends,
  onStatClick,
  scrollRef,
}: DashboardHeaderProps) {
  const handleCardClick = (tabIndex: number) => {
    onStatClick?.(tabIndex, scrollRef);
  };

  return (
    <div className="dashboard-header-card">
      <div className="dashboard-header-content">
        {/* Brand identity section */}
        <div className="dashboard-header-logo-section">
          <div className="dashboard-header-logo-icon">
            <StockSentryLogo size={28} color="white" />
          </div>
          <div className="dashboard-header-brand">
            <span className="dashboard-header-logo-text">StockSentry</span>
            <a
              href="https://retailforge.app"
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-header-subtext"
            >
              By RetailForge <RetailForgeLogo size={14} />
            </a>
          </div>
        </div>

        {/* Stat cards container */}
        <div className="dashboard-header-badges">
          {STAT_CONFIGS.map((stat) => (
            <StatCard
              key={stat.id}
              id={stat.id}
              label={stat.label}
              icon={stat.icon}
              value={stat.getValue(statistics)}
              trend={trends?.[stat.id]}
              tabIndex={stat.tabIndex}
              onClick={handleCardClick}
              scrollRef={scrollRef}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
